import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { faMapLocationDot } from '@fortawesome/pro-regular-svg-icons';
import {
  faCog,
  faFlaskGear,
  faPieChart,
  faPresentation,
  faSliders,
  faTools,
  faWarehouse,
} from '@fortawesome/pro-solid-svg-icons';
import { DemandScienceDefault } from '@frontend/theme';
import { NavItemService } from '@frontend/util/nav-item-access';
import { AuthService } from 'auth';
import { EnvService } from 'frontend-environment';
import { PrimeNG } from 'primeng/config';
import { combineLatest, map } from 'rxjs';

@Component({
  selector: 'ds-data-studio-wrapper',
  template: '<router-outlet></router-outlet>',
  imports: [RouterModule],
  host: {
    '[style.height]': '"100%"',
  },
})
export class DataStudioWrapperComponent {
  constructor() {
    inject(PrimeNG).setConfig({
      theme: {
        preset: DemandScienceDefault,
        options: {
          prefix: '',
          darkModeSelector: 'none',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    });
    const envService = inject(EnvService);
    const authService = inject(AuthService);
    const navItemService = inject(NavItemService);

    navItemService.setAppName('Data Studio');
    navItemService.setUser(
      combineLatest([
        authService.actualUserOktaData$,
        authService.hubUserData$,
      ]).pipe(
        map(([oktaData, hubOrgData]) => {
          return {
            fullname: oktaData?.name || 'Admin User',
            email: oktaData?.email || 'admin@terminus.com',
            couName: hubOrgData?.orgName,
          };
        }),
      ),
    );

    navItemService.setNavItems([
      [
        {
          text: 'Account Hub',
          tooltip: 'Account Hub',
          path: '/data-studio/list-analysis',
          icon: faWarehouse,
          requireHubReportsAccess: 'app.analyze.accounts.attribution',
        },
        {
          text: 'Segments',
          tooltip: 'Segments',
          path: '/data-studio/segments',
          icon: faPieChart,
          // gainsightId: 'sgmt_side_nav',
        },
        {
          title: 'Engagement Signals',
          tooltip: 'Engagement Signals',
          // gainsightId: 'engagement_signals',
          icon: faMapLocationDot,
          items: [
            {
              text: 'Site Visits',
              path: '/data-studio/site-visits',
              requireConfiguration: 'enabled',
              id: 'site-visits',
            },
            {
              text: 'Engagement Spikes',
              path: '/data-studio/engagement-spikes',
            },
            {
              text: 'Intent Navigator',
              path: '/data-studio/intent-navigator',
            },
          ],
        },
      ],
      [
        {
          title: 'Settings',
          tooltip: 'Settings',
          icon: faCog,
          requireHubReportsAccess: 'app.settings',
          items: [
            {
              text: 'Visitor Tracking',
              path: '/data-studio/settings/visitor-tracking',
            },
            {
              text: 'Permissions',
              path: '/data-studio/settings/permissions/team',
              requireHubReportsAccess:
                'app.settings.permissions.teamPermissions',
            },
            {
              text: 'Stages',
              path: '/data-studio/settings/stages',
              requireHubReportsAccess: 'app.settings.stages',
            },
            {
              text: 'Pages And Features',
              path: '/data-studio/settings/pages-and-features',
              requireHubReportsAccess: 'app.settings.pagesAndFeatures',
            },
            {
              text: 'Data Hygiene',
              path: '/data-studio/settings/data-hygiene/orphan-lead-finder',
              requireHubReportsAccess: 'app.settings.dataHygiene',
            },
            {
              text: 'Campaign Scoring',
              path: '/data-studio/settings/campaign-scoring',
              requireHubReportsAccess: 'app.settings.attributionWeight',
            },
            {
              text: 'More Settings',
              path: `${envService.LEGACY_STUDIO_APP_URL}/#/settings/self-configure/field-mapping`,
              requireHubReportsAccess: 'app.settings.selfConfigure',
            },
          ],
        },
      ],
      [
        {
          title: 'DemandScience Labs',
          tooltip: 'DemandScience Labs',
          icon: faFlaskGear,
          items: [
            {
              text: 'Prospect Engine',
              tooltip: 'Prospect Engine',
              path: '/data-studio/prospect-engine',
              requireHubReportsAccess: 'app.dataStudio.prospectEngine',
              id: 'prospect-engine',
            },
          ],
        },
      ],
      [
        {
          title: 'Admin Tools',
          tooltip: 'Admin Tools',
          icon: faTools,
          requireAdmin: true,
          id: 'admin',
          items: [
            {
              text: 'Switch User',
              tooltip: 'Switch User',
              path: 'data-studio/admin/switch-user',
            },
            {
              text: 'Admin Settings',
              path: 'data-studio/admin/settings',
            },
            {
              text: 'Admin Entitlement Toggle',
              path: '',
            },
          ],
        },
        {
          text: 'Internal Configuration',
          tooltip: 'Internal Configuration',
          icon: faSliders,
          path: `${envService.LEGACY_STUDIO_APP_URL}/#/settings/internal-configure/permissions`,
          requireHubPermissions: ['bf.badm'], //[AdminAccessTypes.BFAdmin],
        },
        {
          text: 'Demo Mode',
          tooltip: 'Demo Mode',
          icon: faPresentation,
          path: '/data-studio/demo-config',
          requireHubPermissions: ['bf.badm'], //[AdminAccessTypes.BFAdmin],
        },
      ],
    ]);
  }
}
